/**
 * Typed API client. Every call names its route template literally (checked against the OpenAPI
 * snapshot by client.test.ts), so the UI cannot drift to endpoints or shapes that do not exist.
 */
import { API_BASE, ApiError, authFetch } from './http';
import type * as T from './types.gen';

export type * from './types.gen';

type Query = Record<
  string,
  string | number | boolean | readonly (string | number)[] | null | undefined
>;
interface CallOptions {
  path?: Record<string, string | number>;
  query?: Query;
  body?: unknown;
}

export function buildQuery(q: Query | undefined): string {
  if (!q) return '';
  const parts: string[] = [];
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length)
        parts.push(
          `${encodeURIComponent(k)}=${v.map((x) => encodeURIComponent(String(x))).join(',')}`,
        );
    } else parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

export async function call<R>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  template: string,
  o: CallOptions = {},
): Promise<R> {
  const path = template.replace(/\{(\w+)\}/g, (_m, k: string) => {
    const v = o.path?.[k];
    if (v === undefined) throw new Error(`missing path param ${k} for ${template}`);
    return encodeURIComponent(String(v));
  });
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  };
  if (o.body !== undefined) {
    init.body = JSON.stringify(o.body);
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
  }
  const res = await authFetch(new Request(`${API_BASE}${path}${buildQuery(o.query)}`, init));
  if (!res.ok) throw await ApiError.from(res);
  if (res.status === 204) return undefined as R;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as R;
}

// ------------------------------------------------------------------ shapes the spec leaves open
export interface SessionUser {
  id: number;
  username: string;
  realName: string;
  email: string;
  accessLevel: number;
  protected: boolean;
  authMethod?: string;
}
export interface LoginResult {
  accessToken: string;
  accessExpiresIn: number;
  refreshToken: string;
  user: SessionUser;
}
export interface SessionInfo {
  id: string;
  current: boolean;
  createdAt: string;
  lastUsedAt?: string;
  ip?: string | null;
  userAgent?: string | null;
}
export interface EnumValue {
  code: number;
  name: string;
  label: string | null;
  color: string | null;
}
export type Enums = Record<string, EnumValue[]>;

export interface IssueQuery {
  page?: number | undefined;
  pageSize?: number | undefined;
  projectId?: number | undefined;
  subprojects?: boolean | undefined;
  status?: number[] | undefined;
  severity?: number[] | undefined;
  priority?: number[] | undefined;
  resolution?: number[] | undefined;
  version?: string | undefined;
  targetVersion?: string | undefined;
  fixedInVersion?: string | undefined;
  tagId?: number | undefined;
  monitoredBy?: number | undefined;
  createdFrom?: string | undefined;
  createdTo?: string | undefined;
  handlerId?: number | undefined;
  reporterId?: number | undefined;
  categoryId?: number | undefined;
  search?: string | undefined;
  openOnly?: boolean | undefined;
  filterId?: number | undefined;
  sort?: 'id' | 'updatedAt' | 'createdAt' | 'priority' | 'severity' | 'status' | undefined;
  direction?: 'asc' | 'desc' | undefined;
}

const q = (o: object) => o as Query;

export const api = {
  auth: {
    login: (body: T.LoginDto) => call<LoginResult>('POST', '/api/v2/auth/login', { body }),
    me: () => call<SessionUser>('GET', '/api/v2/auth/me'),
    register: (body: T.RegisterDto) =>
      call<{ status: 'created' | 'pending' }>('POST', '/api/v2/auth/register', { body }),
    activate: (body: T.ActivateDto) => call<void>('POST', '/api/v2/auth/activate', { body }),
    forgot: (body: T.ForgotPasswordDto) =>
      call<void>('POST', '/api/v2/auth/password/forgot', { body }),
    reset: (body: T.ResetPasswordDto) =>
      call<void>('POST', '/api/v2/auth/password/reset', { body }),
    changePassword: (body: T.ChangePasswordDto) =>
      call<void>('POST', '/api/v2/auth/password/change', { body }),
    sessions: () => call<SessionInfo[]>('GET', '/api/v2/auth/sessions'),
    logoutAll: () => call<unknown>('POST', '/api/v2/auth/logout-all'),
  },
  enums: () => call<Enums>('GET', '/api/v2/enums'),
  config: {
    get: (option: string[], projectId?: number) =>
      call<{ option: string; value: unknown }[]>('GET', '/api/v2/config', {
        query: { option, projectId },
      }),
    set: (
      configs: { option: string; value: unknown }[],
      scope: { projectId?: number; userId?: number } = {},
    ) => call<void>('PATCH', '/api/v2/config', { body: { configs, ...scope } }),
    overrides: () => call<T.ConfigOverrideDto[]>('GET', '/api/v2/config/overrides'),
    options: () => call<T.ConfigOptionDto[]>('GET', '/api/v2/config/options'),
  },
  issues: {
    list: (query: IssueQuery) => call<T.IssuePageDto>('GET', '/api/v2/issues', { query: q(query) }),
    get: (id: number) => call<T.IssueDto>('GET', '/api/v2/issues/{id}', { path: { id } }),
    create: (body: T.CreateIssueDto) => call<T.IssueDto>('POST', '/api/v2/issues', { body }),
    update: (id: number, body: T.UpdateIssueDto) =>
      call<T.IssueDto>('PATCH', '/api/v2/issues/{id}', { path: { id }, body }),
    delete: (id: number) => call<void>('DELETE', '/api/v2/issues/{id}', { path: { id } }),
    move: (id: number, body: T.MoveIssueDto) =>
      call<T.IssueDto>('POST', '/api/v2/issues/{id}/move', { path: { id }, body }),
    history: (id: number) =>
      call<T.HistoryEntryDto[]>('GET', '/api/v2/issues/{id}/history', { path: { id } }),
    addNote: (id: number, body: T.AddNoteDto) =>
      call<T.NoteDto>('POST', '/api/v2/issues/{id}/notes', { path: { id }, body }),
    updateNote: (id: number, noteId: number, body: T.UpdateNoteDto) =>
      call<T.NoteDto>('PATCH', '/api/v2/issues/{id}/notes/{noteId}', {
        path: { id, noteId },
        body,
      }),
    deleteNote: (id: number, noteId: number) =>
      call<void>('DELETE', '/api/v2/issues/{id}/notes/{noteId}', { path: { id, noteId } }),
    addRelationship: (id: number, body: T.AddRelationshipDto) =>
      call<{ id: number }>('POST', '/api/v2/issues/{id}/relationships', { path: { id }, body }),
    deleteRelationship: (id: number, relationshipId: number) =>
      call<void>('DELETE', '/api/v2/issues/{id}/relationships/{relationshipId}', {
        path: { id, relationshipId },
      }),
    graph: (id: number, mode: 'relation' | 'dependency', depth?: number) =>
      call<T.RelationshipGraphDto>('GET', '/api/v2/issues/{id}/relationship-graph', {
        path: { id },
        query: { mode, depth },
      }),
    attachTags: (id: number, tags: T.TagRefDto[]) =>
      call<T.TagDto[]>('POST', '/api/v2/issues/{id}/tags', { path: { id }, body: { tags } }),
    detachTag: (id: number, tagId: number) =>
      call<void>('DELETE', '/api/v2/issues/{id}/tags/{tagId}', { path: { id, tagId } }),
    monitor: (id: number, users?: number[]) =>
      call<T.UserRefDto[]>('POST', '/api/v2/issues/{id}/monitors', {
        path: { id },
        body: users ? { users } : {},
      }),
    unmonitor: (id: number, userId: number) =>
      call<void>('DELETE', '/api/v2/issues/{id}/monitors/{userId}', { path: { id, userId } }),
    remind: (id: number, body: T.ReminderDto) =>
      call<{ noteId: number; recipients: number[] }>('POST', '/api/v2/issues/{id}/reminders', {
        path: { id },
        body,
      }),
    revisions: (id: number, noteId?: number) =>
      call<T.RevisionDto[]>('GET', '/api/v2/issues/{id}/revisions', {
        path: { id },
        query: { noteId },
      }),
    files: (id: number) =>
      call<T.AttachmentResponseDto[]>('GET', '/api/v2/issues/{id}/files', { path: { id } }),
    file: (id: number, fileId: number) =>
      call<T.AttachmentDownloadDto>('GET', '/api/v2/issues/{id}/files/{fileId}', {
        path: { id, fileId },
      }),
    deleteFile: (id: number, fileId: number) =>
      call<void>('DELETE', '/api/v2/issues/{id}/files/{fileId}', { path: { id, fileId } }),
  },
  notes: {
    locate: (noteId: number) =>
      call<T.NoteLocationDto>('GET', '/api/v2/notes/{noteId}', { path: { noteId } }),
    revisions: (noteId: number) =>
      call<T.RevisionDto[]>('GET', '/api/v2/notes/{noteId}/revisions', { path: { noteId } }),
  },
  revisions: {
    get: (id: string) => call<T.RevisionDto>('GET', '/api/v2/revisions/{id}', { path: { id } }),
    drop: (id: string) => call<void>('DELETE', '/api/v2/revisions/{id}', { path: { id } }),
  },
  files: {
    locate: (fileId: number) =>
      call<T.FileLocationDto>('GET', '/api/v2/files/{fileId}', { path: { fileId } }),
  },
  tags: {
    search: (prefix: string) => call<T.TagDto[]>('GET', '/api/v2/tags', { query: { prefix } }),
    list: (query: { page?: number; pageSize?: number; search?: string }) =>
      call<T.TagDetailPageDto>('GET', '/api/v2/tags/list', { query: q(query) }),
    get: (tagId: number) =>
      call<T.TagDetailDto>('GET', '/api/v2/tags/{tagId}', { path: { tagId } }),
    create: (body: T.CreateTagDto) => call<T.TagDetailDto>('POST', '/api/v2/tags', { body }),
    update: (tagId: number, body: T.UpdateTagDto) =>
      call<T.TagDetailDto>('PATCH', '/api/v2/tags/{tagId}', { path: { tagId }, body }),
    delete: (tagId: number) => call<void>('DELETE', '/api/v2/tags/{tagId}', { path: { tagId } }),
  },
  filters: {
    list: () => call<T.FilterResponseDto[]>('GET', '/api/v2/filters'),
    get: (id: number) => call<T.FilterResponseDto>('GET', '/api/v2/filters/{id}', { path: { id } }),
    create: (body: T.SaveFilterDto) =>
      call<T.FilterResponseDto>('POST', '/api/v2/filters', { body }),
    update: (id: number, body: T.UpdateFilterDto) =>
      call<T.FilterResponseDto>('PATCH', '/api/v2/filters/{id}', { path: { id }, body }),
    delete: (id: number) => call<void>('DELETE', '/api/v2/filters/{id}', { path: { id } }),
  },
  projects: {
    list: () => call<T.ProjectDto[]>('GET', '/api/v2/projects'),
    get: (id: number) => call<T.ProjectDto>('GET', '/api/v2/projects/{id}', { path: { id } }),
    create: (body: T.CreateProjectDto) => call<T.ProjectDto>('POST', '/api/v2/projects', { body }),
    update: (id: number, body: T.UpdateProjectDto) =>
      call<T.ProjectDto>('PATCH', '/api/v2/projects/{id}', { path: { id }, body }),
    delete: (id: number) => call<void>('DELETE', '/api/v2/projects/{id}', { path: { id } }),
    versions: (
      id: number,
      query: { released?: boolean; obsolete?: boolean; inherit?: boolean } = {},
    ) =>
      call<T.VersionDto[]>('GET', '/api/v2/projects/{id}/versions', {
        path: { id },
        query: q(query),
      }),
    version: (id: number, versionId: number) =>
      call<T.VersionDto>('GET', '/api/v2/projects/{id}/versions/{versionId}', {
        path: { id, versionId },
      }),
    addVersion: (id: number, body: T.CreateVersionDto) =>
      call<T.VersionDto>('POST', '/api/v2/projects/{id}/versions', { path: { id }, body }),
    updateVersion: (id: number, versionId: number, body: T.UpdateVersionDto) =>
      call<T.VersionDto>('PATCH', '/api/v2/projects/{id}/versions/{versionId}', {
        path: { id, versionId },
        body,
      }),
    deleteVersion: (id: number, versionId: number) =>
      call<void>('DELETE', '/api/v2/projects/{id}/versions/{versionId}', {
        path: { id, versionId },
      }),
    categories: (id: number) =>
      call<T.CategoryDto[]>('GET', '/api/v2/projects/{id}/categories', { path: { id } }),
    category: (id: number, categoryId: number) =>
      call<T.CategoryDto>('GET', '/api/v2/projects/{id}/categories/{categoryId}', {
        path: { id, categoryId },
      }),
    addCategory: (id: number, body: T.CreateCategoryDto) =>
      call<T.CategoryDto>('POST', '/api/v2/projects/{id}/categories', { path: { id }, body }),
    updateCategory: (id: number, categoryId: number, body: T.UpdateCategoryDto) =>
      call<T.CategoryDto>('PATCH', '/api/v2/projects/{id}/categories/{categoryId}', {
        path: { id, categoryId },
        body,
      }),
    deleteCategory: (id: number, categoryId: number, moveTo?: number) =>
      call<void>('DELETE', '/api/v2/projects/{id}/categories/{categoryId}', {
        path: { id, categoryId },
        query: { moveTo },
      }),
    addSubproject: (id: number, body: T.AddSubprojectDto) =>
      call<void>('POST', '/api/v2/projects/{id}/subprojects', { path: { id }, body }),
    updateSubproject: (id: number, subprojectId: number, body: T.UpdateSubprojectDto) =>
      call<void>('PATCH', '/api/v2/projects/{id}/subprojects/{subprojectId}', {
        path: { id, subprojectId },
        body,
      }),
    removeSubproject: (id: number, subprojectId: number) =>
      call<void>('DELETE', '/api/v2/projects/{id}/subprojects/{subprojectId}', {
        path: { id, subprojectId },
      }),
    users: (id: number, minLevel?: number) =>
      call<T.ProjectUserDto[]>('GET', '/api/v2/projects/{id}/users', {
        path: { id },
        query: { minLevel },
      }),
    setMember: (id: number, body: T.SetMemberDto) =>
      call<void>('PUT', '/api/v2/projects/{id}/users', { path: { id }, body }),
    removeMember: (id: number, userId: number) =>
      call<void>('DELETE', '/api/v2/projects/{id}/users/{userId}', { path: { id, userId } }),
    handlers: (id: number) =>
      call<T.ProjectUserDto[]>('GET', '/api/v2/projects/{id}/handlers', { path: { id } }),
    fields: (id: number) =>
      call<T.ProjectFieldDto[]>('GET', '/api/v2/projects/{id}/fields', { path: { id } }),
    linkField: (id: number, fieldId: number, sequence?: number) =>
      call<void>('POST', '/api/v2/projects/{id}/fields/{fieldId}', {
        path: { id, fieldId },
        body: sequence === undefined ? {} : { sequence },
      }),
    unlinkField: (id: number, fieldId: number) =>
      call<void>('DELETE', '/api/v2/projects/{id}/fields/{fieldId}', { path: { id, fieldId } }),
  },
  customFields: {
    list: () => call<T.CustomFieldDto[]>('GET', '/api/v2/custom-fields'),
    get: (id: number) =>
      call<T.CustomFieldDetailDto>('GET', '/api/v2/custom-fields/{id}', { path: { id } }),
    create: (body: T.CreateCustomFieldDto) =>
      call<T.CustomFieldDetailDto>('POST', '/api/v2/custom-fields', { body }),
    update: (id: number, body: T.UpdateCustomFieldDto) =>
      call<T.CustomFieldDetailDto>('PATCH', '/api/v2/custom-fields/{id}', { path: { id }, body }),
    delete: (id: number) => call<void>('DELETE', '/api/v2/custom-fields/{id}', { path: { id } }),
  },
  users: {
    list: (query: { page?: number; pageSize?: number; search?: string; enabled?: boolean }) =>
      call<T.UserPageDto>('GET', '/api/v2/users', { query: q(query) }),
    get: (id: number) => call<T.UserDto>('GET', '/api/v2/users/{id}', { path: { id } }),
    me: () => call<T.UserDto>('GET', '/api/v2/users/me'),
    create: (body: T.CreateUserDto) => call<T.UserDto>('POST', '/api/v2/users', { body }),
    update: (id: number, body: T.UpdateUserDto) =>
      call<T.UserDto>('PATCH', '/api/v2/users/{id}', { path: { id }, body }),
    delete: (id: number) => call<void>('DELETE', '/api/v2/users/{id}', { path: { id } }),
    resetPassword: (id: number) => call<void>('PUT', '/api/v2/users/{id}/reset', { path: { id } }),
    tokens: () => call<T.TokenResponseDto[]>('GET', '/api/v2/users/me/tokens'),
    createToken: (body: T.CreateTokenDto) =>
      call<T.CreatedTokenDto>('POST', '/api/v2/users/me/token', { body }),
    revokeToken: (tokenId: number) =>
      call<void>('DELETE', '/api/v2/users/me/token/{tokenId}', { path: { tokenId } }),
    avatarUrl: (id: number, size = 80) => `${API_BASE}/api/v2/users/${id}/avatar?size=${size}`,
  },
  preferences: {
    get: (projectId?: number) =>
      call<T.PreferencesDto>('GET', '/api/v2/users/me/preferences', { query: { projectId } }),
    save: (body: T.PreferencesInputDto, projectId?: number) =>
      call<T.PreferencesDto>('PUT', '/api/v2/users/me/preferences', { query: { projectId }, body }),
    reset: (projectId?: number) =>
      call<T.PreferencesDto>('POST', '/api/v2/users/me/preferences/reset', {
        query: { projectId },
      }),
  },
  profiles: {
    list: () => call<T.ProfileDto[]>('GET', '/api/v2/profiles'),
    globals: () => call<T.ProfileDto[]>('GET', '/api/v2/profiles/global'),
    get: (id: number) => call<T.ProfileDto>('GET', '/api/v2/profiles/{id}', { path: { id } }),
    create: (body: T.CreateProfileDto) => call<T.ProfileDto>('POST', '/api/v2/profiles', { body }),
    update: (id: number, body: T.UpdateProfileDto) =>
      call<T.ProfileDto>('PATCH', '/api/v2/profiles/{id}', { path: { id }, body }),
    delete: (id: number) => call<void>('DELETE', '/api/v2/profiles/{id}', { path: { id } }),
  },
  summary: (projectId?: number, days = 30) =>
    call<T.SummaryDto>('GET', '/api/v2/summary', { query: { projectId, days } }),
};

/** Upload files to an issue (multipart; same limits as legacy: max_file_size, allowed/disallowed types). */
export async function uploadFiles(
  issueId: number,
  files: readonly File[],
): Promise<T.AttachmentResponseDto[]> {
  const form = new FormData();
  for (const f of files) form.append('files', f, f.name);
  const res = await authFetch(
    new Request(`${API_BASE}/api/v2/issues/${issueId}/files`, {
      method: 'POST',
      body: form,
      credentials: 'include',
    }),
  );
  if (!res.ok) throw await ApiError.from(res);
  return (await res.json()) as T.AttachmentResponseDto[];
}
